import { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiDownload, FiEye, FiMail, FiPhone, FiMapPin, FiCalendar, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { universityService, getUploadUrl } from '../services/apiService';
import * as XLSX from 'xlsx';

const UniversitiesList = () => {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 1,
    totalItems: 0
  });
  const fetchUniversities = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        status: filterStatus, // Always send status, including 'all'
        page: pagination.current,
        limit: 20,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };

      if (filterVerified === 'verified') {
        params.isVerified = true;
      } else if (filterVerified === 'unverified') {
        params.isVerified = false;
      }

      const response = await universityService.getAllUniversities(params);
      
      if (response && response.data) {
        setUniversities(response.data);
        if (response.pagination) {
          setPagination({
            current: response.pagination.current,
            total: response.pagination.total,
            totalItems: response.pagination.totalItems
          });
        }
      }
    } catch (err) {
      console.error('Error fetching universities:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterVerified, pagination.current]);

  useEffect(() => {
    fetchUniversities();
  }, [fetchUniversities]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchUniversities();
      return;
    }

    try {
      setLoading(true);
      const response = await universityService.getAllUniversities({
        search: searchTerm,
        page: 1,
        limit: 20
      });
      
      if (response && response.data) {
        setUniversities(response.data);
        if (response.pagination) {
          setPagination({
            current: response.pagination.current,
            total: response.pagination.total,
            totalItems: response.pagination.totalItems
          });
        }
      }
    } catch (err) {
      console.error('Error searching universities:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await universityService.getAllUniversities({
        limit: 10000, // Get all universities
        isVerified: true
      });
      
      const allUniversities = response.data || [];
      
      if (allUniversities.length === 0) {
        alert('No universities to export');
        return;
      }

      const exportData = allUniversities.map(uni => ({
        'Institute Name': uni.instituteName || '',
        'University': uni.university || '',
        'Contact Person': uni.contactPerson || '',
        'Salutation': uni.salutation || '',
        'Status': uni.status || '',
        'Email': uni.email || '',
        'Mobile': uni.mobileNumber || '',
        'City': uni.instituteCity || '',
        'State': uni.state || '',
        'Country': uni.country || '',
        'Website': uni.website || '',
        'Verified': uni.isVerified ? 'Yes' : 'No',
        'Join Date': uni.legacyJoinDate ? new Date(uni.legacyJoinDate).toLocaleDateString() : (new Date(uni.createdAt).toLocaleDateString()),
        'Legacy ID': uni.legacyId || '',
        'Legacy Points': uni.legacyPoints || 0
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Universities');
      XLSX.writeFile(wb, `universities_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error downloading universities:', err);
      alert('Failed to download universities list');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current: newPage }));
  };

  const filteredUniversities = universities.filter(uni => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        uni.instituteName?.toLowerCase().includes(search) ||
        uni.university?.toLowerCase().includes(search) ||
        uni.email?.toLowerCase().includes(search) ||
        uni.contactPerson?.toLowerCase().includes(search) ||
        uni.instituteCity?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Registered Universities</h1>
        <p className="mt-1 text-sm text-gray-600">
          Total: {pagination.totalItems} universities
        </p>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name, email, city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
              />
              <FiSearch className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="Faculty">Faculty</option>
              <option value="Administrator">Administrator</option>
              <option value="Principal">Principal</option>
              <option value="Dean">Dean</option>
              <option value="HOD">HOD</option>
            </select>
          </div>

          {/* Verification Filter */}
          <div>
            <select
              value={filterVerified}
              onChange={(e) => setFilterVerified(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
            >
              <option value="all">All Universities</option>
              <option value="verified">Verified Only</option>
              <option value="unverified">Unverified Only</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 flex justify-between items-center">
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-[#166534] text-white rounded-lg hover:bg-[#0e3d1f] transition-colors flex items-center"
          >
            <FiSearch className="mr-2" />
            Search
          </button>
          <button
            onClick={downloadExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
          >
            <FiDownload className="mr-2" />
            Export to Excel
          </button>
        </div>
      </div>

      {/* Universities Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#166534]"></div>
            <p className="mt-2 text-gray-600">Loading universities...</p>
          </div>
        ) : filteredUniversities.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No universities found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Institute
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Person
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Verified
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUniversities.map((uni) => (
                    <tr key={uni._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {uni.logo ? (
                            <img 
                              src={getUploadUrl(uni.logo)} 
                              alt={`${uni.instituteName} logo`}
                              className="w-10 h-10 rounded-full mr-3 object-cover border-2 border-gray-200"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full mr-3 bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-500 text-xs font-medium">
                                {uni.instituteName?.charAt(0) || 'U'}
                              </span>
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{uni.instituteName}</div>
                            <div className="text-sm text-gray-500">{uni.university}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{uni.salutation} {uni.contactPerson}</div>
                        <div className="text-sm text-gray-500">{uni.status}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{uni.email}</div>
                        <div className="text-sm text-gray-500">{uni.mobileNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{uni.instituteCity}</div>
                        <div className="text-sm text-gray-500">{uni.state}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {uni.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {uni.isVerified ? (
                          <span className="flex items-center text-green-600">
                            <FiCheckCircle className="mr-1" />
                            <span className="text-sm">Yes</span>
                          </span>
                        ) : (
                          <span className="flex items-center text-yellow-600">
                            <FiXCircle className="mr-1" />
                            <span className="text-sm">No</span>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => setSelectedUniversity(uni)}
                          className="text-[#166534] hover:text-[#0e3d1f] flex items-center"
                        >
                          <FiEye className="mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.current - 1)}
                    disabled={pagination.current === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.current + 1)}
                    disabled={pagination.current === pagination.total}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(pagination.current - 1) * 20 + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(pagination.current * 20, pagination.totalItems)}</span> of{' '}
                      <span className="font-medium">{pagination.totalItems}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(pagination.current - 1)}
                        disabled={pagination.current === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      {[...Array(Math.min(pagination.total, 5))].map((_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pagination.current === pageNum
                                ? 'z-10 bg-[#166534] border-[#166534] text-white'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => handlePageChange(pagination.current + 1)}
                        disabled={pagination.current === pagination.total}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedUniversity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  {selectedUniversity.logo ? (
                    <img 
                      src={getUploadUrl(selectedUniversity.logo)} 
                      alt={`${selectedUniversity.instituteName} logo`}
                      className="w-16 h-16 rounded-full mr-4 object-cover border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full mr-4 bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-500 text-lg font-medium">
                        {selectedUniversity.instituteName?.charAt(0) || 'U'}
                      </span>
                    </div>
                  )}
                  <h2 className="text-2xl font-semibold text-gray-900">University Details</h2>
                </div>
                <button
                  onClick={() => setSelectedUniversity(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Logo and Campus Photo Section */}
              {(selectedUniversity.logo || selectedUniversity.campusPhoto) && (
                <div className="mb-6 grid grid-cols-2 gap-4">
                  {selectedUniversity.logo && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">University Logo</p>
                      <img 
                        src={getUploadUrl(selectedUniversity.logo)} 
                        alt="University Logo"
                        className="w-full h-32 object-contain border rounded-lg bg-gray-50"
                      />
                    </div>
                  )}
                  {selectedUniversity.campusPhoto && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Campus Photo</p>
                      <img 
                        src={getUploadUrl(selectedUniversity.campusPhoto)} 
                        alt="Campus Photo"
                        className="w-full h-32 object-cover border rounded-lg"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Institute Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Institute Name</p>
                      <p className="text-sm font-medium">{selectedUniversity.instituteName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">University</p>
                      <p className="text-sm font-medium">{selectedUniversity.university}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Website</p>
                      <a href={selectedUniversity.website} target="_blank" rel="noopener noreferrer" className="text-sm text-[#166534] hover:underline">
                        {selectedUniversity.website}
                      </a>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <p className="text-sm font-medium">{selectedUniversity.status}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Contact Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Contact Person</p>
                      <p className="text-sm font-medium">{selectedUniversity.salutation} {selectedUniversity.contactPerson}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="text-sm font-medium flex items-center">
                        <FiMail className="mr-1" />
                        {selectedUniversity.email}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mobile</p>
                      <p className="text-sm font-medium flex items-center">
                        <FiPhone className="mr-1" />
                        {selectedUniversity.mobileNumber}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Location</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">City</p>
                      <p className="text-sm font-medium flex items-center">
                        <FiMapPin className="mr-1" />
                        {selectedUniversity.instituteCity}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">State</p>
                      <p className="text-sm font-medium">{selectedUniversity.state}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Country</p>
                      <p className="text-sm font-medium">{selectedUniversity.country}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Zip Code</p>
                      <p className="text-sm font-medium">{selectedUniversity.zipCode}</p>
                    </div>
                  </div>
                  {selectedUniversity.addressLine1 && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="text-sm font-medium">{selectedUniversity.addressLine1}</p>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Additional Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Verified</p>
                      <p className="text-sm font-medium">{selectedUniversity.isVerified ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Joined</p>
                      <p className="text-sm font-medium flex items-center">
                        <FiCalendar className="mr-1" />
                        {selectedUniversity.legacyJoinDate 
                          ? new Date(selectedUniversity.legacyJoinDate).toLocaleDateString()
                          : new Date(selectedUniversity.createdAt).toLocaleDateString()
                        }
                      </p>
                    </div>
                    {selectedUniversity.legacyId && (
                      <div>
                        <p className="text-sm text-gray-500">Legacy ID</p>
                        <p className="text-sm font-medium">{selectedUniversity.legacyId}</p>
                      </div>
                    )}
                    {selectedUniversity.legacyPoints > 0 && (
                      <div>
                        <p className="text-sm text-gray-500">Legacy Points</p>
                        <p className="text-sm font-medium">{selectedUniversity.legacyPoints}</p>
                      </div>
                    )}
                  </div>
                  {selectedUniversity.description && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">Description</p>
                      <p className="text-sm">{selectedUniversity.description}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedUniversity(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversitiesList;
